import { getDisplayName, getSortOrder, headerLabelToOriginalKey } from "./cache";

export const PVT_TEST_SOURCE = "pvt";
const PVT_NAV_SESSION_KEY = "fluidsdata.pvtTestNav";
const PVT_WHOLE_NUMBER_FIELD_KEYS = new Set([
  "testNumber",
]);

const PVT_API_TEST_TYPE_TO_PLURAL = {
  ccetest: "ccetests",
  dltest: "dltests",
  viscositytest: "viscositytests",
  cvdtest: "cvdtests",
  separatortest: "separatortests",
  compositiontests: "compositiontests",
};

function formatPvtHeaderValue(fieldKey, value) {
  if (
    PVT_WHOLE_NUMBER_FIELD_KEYS.has(fieldKey) &&
    typeof value === "number" &&
    Number.isFinite(value)
  ) {
    return Math.round(value);
  }
  return value;
}

function getPvtHeaderFieldDigits(fieldKey, value) {
  if (PVT_WHOLE_NUMBER_FIELD_KEYS.has(fieldKey)) {
    return 0;
  }
  return typeof value === "number" ? 2 : 0;
}

export function buildPvtPlotTrace(trace) {
  return {
    ...trace,
    mode: "lines",
    marker: {
      ...(trace.marker || {}),
      size: 0,
      opacity: 0,
    },
  };
}

export const PVT_DL_CHART_EXCLUDED_KEYS = new Set(["cumulativeStockTankGOR"]);

export function filterPvtDlChartVariables(keys) {
  if (!Array.isArray(keys)) {
    return [];
  }
  return keys.filter((key) => !PVT_DL_CHART_EXCLUDED_KEYS.has(key));
}

export function getPvtTestsDropdownEndpoint(sampleId) {
  return `public/api/samples/${sampleId}/pvt/tests`;
}

export function getPvtTestListEndpoint(sampleId, testArrayKey) {
  const raw = String(testArrayKey || "").toLowerCase();
  const plural = PVT_API_TEST_TYPE_TO_PLURAL[raw] || (raw.endsWith("s") ? raw : `${raw}s`);
  return `public/api/samples/${sampleId}/pvt/${plural}`;
}

const TEST_ARRAY_TYPE_ALIASES = {
  ccetests: ["ccetest", "ccetests"],
  dltests: ["dltest", "dltests"],
  viscositytests: ["viscositytest", "viscositytests"],
  cvdtests: ["cvdtest", "cvdtests"],
  separatortests: ["separatortest", "separatortests"],
  compositiontests: ["compositiontest", "compositiontests"],
};

function pvtSampleMetaFromPayload(data) {
  if (!data || typeof data !== "object") {
    return null;
  }
  const nested = data.sampleMeta || data.pvtSample || data.PvtSample;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...data, ...nested };
  }
  return data;
}

export function getPvtTestsFromPayload(data, testArrayKey) {
  if (!data || typeof data !== "object" || !testArrayKey) {
    return [];
  }
  const singularKey = String(testArrayKey).replace(/s$/, "");
  const singular = data[singularKey];
  if (singular && typeof singular === "object" && !Array.isArray(singular)) {
    return [singular];
  }
  if (Array.isArray(data[testArrayKey]) && data[testArrayKey].length) {
    return data[testArrayKey];
  }
  const aliases = TEST_ARRAY_TYPE_ALIASES[testArrayKey] || [String(testArrayKey).toLowerCase()];
  if (!Array.isArray(data.allTests)) {
    return [];
  }
  return data.allTests.filter((test) => {
    const type = String(test.testType || test.TestType || test.test_type || "").toLowerCase();
    const mapped = PVT_API_TEST_TYPE_TO_PLURAL[type] || type;
    return aliases.includes(type) || aliases.includes(mapped);
  });
}

function matchPvtTestById(tests, testId) {
  if (!Array.isArray(tests) || !tests.length) {
    return null;
  }
  if (testId) {
    const matched = tests.find(
      (test) => String(test.ID || test.id || test._id) === String(testId)
    );
    if (matched) {
      return matched;
    }
  }
  return tests[0] || null;
}

export function normalizePvtDropdownTests(allTests) {
  if (!Array.isArray(allTests)) {
    return [];
  }

  return allTests
    .map((test, index) => {
      const rawType = test.testType || test.TestType || test.test_type;
      const pluralType = PVT_API_TEST_TYPE_TO_PLURAL[rawType] || rawType;
      const testId = test.ID || test.id || test._id;
      return {
        ...test,
        testType: pluralType,
        TestType: pluralType,
        test_type: pluralType,
        ID: testId,
        id: testId,
        index,
      };
    })
    .filter((test) => {
      const type = String(test.testType || "").toLowerCase();
      return type !== "compositiontests" && type !== "compositiontest";
    });
}

export function appendRouteQuery(path, query) {
  if (!query || typeof query !== "object") {
    return path;
  }
  const searchParams = new URLSearchParams();
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (value == null || value === "") {
      return;
    }
    const normalized = Array.isArray(value) ? value[0] : value;
    if (normalized != null && String(normalized) !== "") {
      searchParams.set(key, String(normalized));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function markPvtTestNavigation(apiSampleId) {
  if (!apiSampleId) {
    return;
  }
  sessionStorage.setItem(PVT_NAV_SESSION_KEY, String(apiSampleId));
}

export function clearPvtTestNavigation() {
  sessionStorage.removeItem(PVT_NAV_SESSION_KEY);
}

export function getPvtTestNavigationContext(route) {
  const routeSampleId = route?.params?.sampleId
    ? String(route.params.sampleId)
    : null;
  const queryParentSampleId = route?.query?.parentSampleId
    ? String(route.query.parentSampleId)
    : null;
  const source = route?.query?.source;

  if (source === PVT_TEST_SOURCE) {
    return {
      apiSampleId: queryParentSampleId || routeSampleId,
      parentSampleId: queryParentSampleId || routeSampleId,
    };
  }

  if (!routeSampleId) {
    return null;
  }

  try {
    const storedSampleId = sessionStorage.getItem(PVT_NAV_SESSION_KEY);
    if (storedSampleId && storedSampleId === routeSampleId) {
      return {
        apiSampleId: storedSampleId,
        parentSampleId: storedSampleId,
      };
    }
  } catch (_) {
    return null;
  }

  return null;
}

export function isPvtTestRoute(route) {
  return getPvtTestNavigationContext(route) != null;
}

export function getPvtParentSampleId(route) {
  return getPvtTestNavigationContext(route)?.parentSampleId || null;
}

export function getPvtApiSampleId(route) {
  return getPvtTestNavigationContext(route)?.apiSampleId || null;
}

export function buildPvtTestRouteQuery(parentSampleId) {
  const query = { source: PVT_TEST_SOURCE };
  if (parentSampleId) {
    query.parentSampleId = parentSampleId;
  }
  return query;
}

export function preservePvtRouteQuery(route) {
  if (!isPvtTestRoute(route)) {
    return {};
  }
  return buildPvtTestRouteQuery(getPvtParentSampleId(route));
}

const ROUTE_SEGMENT_TO_TEST_ARRAY = {
  compositiontests: "compositiontests",
  ccetest: "ccetests",
  dltest: "dltests",
  cvdtest: "cvdtests",
  separatortest: "separatortests",
  viscositytest: "viscositytests",
};

export function getTestArrayKeyFromRoutePath(path) {
  const match = String(path || "").match(
    /\/(compositiontests|ccetest|dltest|cvdtest|separatortest|viscositytest)\//
  );
  if (!match) {
    return null;
  }
  return ROUTE_SEGMENT_TO_TEST_ARRAY[match[1]] || null;
}

export function normalizeTestHeader(pageHeader) {
  if (!pageHeader) {
    return [];
  }
  if (Array.isArray(pageHeader)) {
    return pageHeader;
  }
  if (typeof pageHeader === "object") {
    return Object.entries(pageHeader).map(([key, value]) => ({ key, value }));
  }
  return [];
}

function fallbackNumericDigits(value) {
  return typeof value === "number" && Number.isFinite(value) ? 2 : 0;
}

export async function enrichHeaderFieldsWithMeta(fields, tableName) {
  if (!Array.isArray(fields) || !tableName) {
    return Array.isArray(fields) ? fields : [];
  }

  const enriched = [];
  for (const field of fields) {
    const rawKey = field.originalKey || field.key;
    const key = headerLabelToOriginalKey(rawKey);
    let displayName = field.displayName || rawKey;
    let digits = field.digits;
    try {
      const meta = await getDisplayName(tableName, key);
      if (meta.displayName) {
        displayName = meta.displayName;
      }
      if (meta.digits != null) {
        digits = meta.digits;
      }
    } catch {
      // Keep fallback label/digits when metadata has no match.
    }
    if (digits == null || digits === "") {
      digits = fallbackNumericDigits(field.value);
    }
    enriched.push({
      ...field,
      originalKey: key,
      key: field.key ? headerLabelToOriginalKey(field.key) : key,
      displayName,
      digits,
    });
  }
  return enriched;
}

export async function buildHeaderFromPageData(pageHeader, tableName, sortPage, sortPosition) {
  const normalizedHeader = normalizeTestHeader(pageHeader);
  if (!normalizedHeader.length) {
    return [];
  }

  let sortOrder = [];
  try {
    const raw = await getSortOrder(tableName, sortPage, sortPosition);
    sortOrder = Array.isArray(raw) ? raw : [];
  } catch {
    sortOrder = [];
  }

  const sortedHeader = [...normalizedHeader].sort((a, b) => {
    const keyA = headerLabelToOriginalKey(a.key);
    const keyB = headerLabelToOriginalKey(b.key);
    const indexA = sortOrder.indexOf(a.key);
    const indexB = sortOrder.indexOf(b.key);
    const altA = sortOrder.indexOf(keyA);
    const altB = sortOrder.indexOf(keyB);
    const ia = indexA === -1 ? (altA === -1 ? Number.MAX_SAFE_INTEGER : altA) : indexA;
    const ib = indexB === -1 ? (altB === -1 ? Number.MAX_SAFE_INTEGER : altB) : indexB;
    return ia - ib;
  });

  return enrichHeaderFieldsWithMeta(
    sortedHeader.map((item) => ({
      originalKey: item.key,
      key: item.key,
      displayName: item.displayName || item.key,
      value: item.value,
      digits: item.digits,
    })),
    tableName
  );
}

export function buildPvtViscosityHeaderFields(sampleData, viscosityTest) {
  const fields = [];
  const pushField = (displayName, originalKey, value, unit = "") => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    const formattedValue = formatPvtHeaderValue(originalKey, value);
    fields.push({
      originalKey,
      key: originalKey,
      displayName,
      value: formattedValue,
      digits: getPvtHeaderFieldDigits(originalKey, formattedValue),
    });
    if (unit) {
      fields.push({
        originalKey: `${originalKey}_UOM`,
        key: `${originalKey}_UOM`,
        displayName: `${displayName} UOM`,
        value: unit,
        digits: 0,
      });
    }
  };

  pushField("Sample ID", "fluidSampleID", sampleData?.fluidSampleID || sampleData?.displayName);
  pushField("Fluid Type", "fluidType", sampleData?.fluidType || sampleData?.reservoirFluid);
  pushField("Model", "modelName", sampleData?.modelName);

  const viscosityParams = sampleData?.predictionParams?.viscosity;
  if (viscosityParams?.paramFileName) {
    pushField("Viscosity Param File", "viscosityParamFileName", viscosityParams.paramFileName);
  }

  const viscosityRegion = sampleData?.regions?.viscosity;
  if (viscosityRegion?.label) {
    const regionValue = viscosityRegion.tier
      ? `${viscosityRegion.label} (Tier ${viscosityRegion.tier})`
      : viscosityRegion.label;
    pushField("Viscosity Region", "viscosityRegion", regionValue);
  }

  if (viscosityTest) {
    pushField("Test Number", "testNumber", viscosityTest.testNumber);
    pushField(
      "Test Temperature",
      "testTemperature",
      viscosityTest.testTemperature,
      viscosityTest.testTemperature_UOM || ""
    );
    pushField(
      "Saturation Pressure",
      "saturationPressure",
      viscosityTest.saturationPressure,
      viscosityTest.saturationPressure_UOM || ""
    );
  }

  const location = sampleData?.location || {};
  pushField("Field", "field", location.field);
  pushField("Well", "well", location.well);
  pushField("Asset", "asset", location.asset);

  return fields;
}

export function extractViscosityTestFromResponse(data, testId) {
  if (!data || typeof data !== "object") {
    return { sampleMeta: null, viscosityTest: null };
  }

  return {
    sampleMeta: pvtSampleMetaFromPayload(data),
    viscosityTest: matchPvtTestById(
      getPvtTestsFromPayload(data, "viscositytests"),
      testId
    ),
  };
}

export function extractCceTestFromResponse(data, testId) {
  if (!data || typeof data !== "object") {
    return { sampleMeta: null, cceTest: null };
  }

  return {
    sampleMeta: pvtSampleMetaFromPayload(data),
    cceTest: matchPvtTestById(
      getPvtTestsFromPayload(data, "ccetests"),
      testId
    ),
  };
}

export function buildPvtCceHeaderFields(sampleData, cceTest) {
  const fields = [];
  const pushField = (displayName, originalKey, value, unit = "") => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    const formattedValue = formatPvtHeaderValue(originalKey, value);
    fields.push({
      originalKey,
      displayName,
      value: formattedValue,
      digits: getPvtHeaderFieldDigits(originalKey, formattedValue),
    });
    if (unit) {
      fields.push({
        originalKey: `${originalKey}_UOM`,
        displayName: `${displayName} UOM`,
        value: unit,
        digits: 0,
      });
    }
  };

  pushField("Sample ID", "fluidSampleID", sampleData?.fluidSampleID || sampleData?.displayName);
  pushField("Fluid Type", "fluidType", sampleData?.fluidType || sampleData?.reservoirFluid);
  pushField("Model", "modelName", sampleData?.modelName);

  const cceParams = sampleData?.predictionParams?.cce;
  if (cceParams?.paramFileName) {
    pushField("CCE Param File", "cceParamFileName", cceParams.paramFileName);
  }

  const cceRegion = sampleData?.regions?.cce;
  if (cceRegion?.label) {
    const regionValue = cceRegion.tier
      ? `${cceRegion.label} (Tier ${cceRegion.tier})`
      : cceRegion.label;
    pushField("CCE Region", "cceRegion", regionValue);
  }

  if (cceTest) {
    pushField("Test Number", "testNumber", cceTest.testNumber);
    pushField(
      "Test Temperature",
      "testTemperature",
      cceTest.testTemperature,
      cceTest.testTemperature_UOM || ""
    );
    pushField(
      "Saturation Pressure",
      "saturationPressure",
      cceTest.saturationPressure,
      cceTest.saturationPressure_UOM || ""
    );
  }

  const location = sampleData?.location || {};
  pushField("Field", "field", location.field);
  pushField("Well", "well", location.well);
  pushField("Asset", "asset", location.asset);

  return fields;
}

export function extractDlTestFromResponse(data, testId) {
  if (!data || typeof data !== "object") {
    return { sampleMeta: null, dlTest: null };
  }

  return {
    sampleMeta: pvtSampleMetaFromPayload(data),
    dlTest: matchPvtTestById(
      getPvtTestsFromPayload(data, "dltests"),
      testId
    ),
  };
}

export function buildPvtDlHeaderFields(sampleData, dlTest) {
  const fields = [];
  const pushField = (displayName, originalKey, value, unit = "") => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    const formattedValue = formatPvtHeaderValue(originalKey, value);
    fields.push({
      originalKey,
      displayName,
      value: formattedValue,
      digits: getPvtHeaderFieldDigits(originalKey, formattedValue),
    });
    if (unit) {
      fields.push({
        originalKey: `${originalKey}_UOM`,
        displayName: `${displayName} UOM`,
        value: unit,
        digits: 0,
      });
    }
  };

  pushField("Sample ID", "fluidSampleID", sampleData?.fluidSampleID || sampleData?.displayName);
  pushField("Fluid Type", "fluidType", sampleData?.fluidType || sampleData?.reservoirFluid);
  pushField("Model", "modelName", sampleData?.modelName);

  const dlParams = sampleData?.predictionParams?.dv;
  if (dlParams?.paramFileName) {
    pushField("DL Param File", "dlParamFileName", dlParams.paramFileName);
  }

  const dlRegion = sampleData?.regions?.dv;
  if (dlRegion?.label) {
    const regionValue = dlRegion.tier
      ? `${dlRegion.label} (Tier ${dlRegion.tier})`
      : dlRegion.label;
    pushField("DL Region", "dlRegion", regionValue);
  }

  if (dlTest) {
    pushField("Test Number", "testNumber", dlTest.testNumber);
    pushField(
      "Test Temperature",
      "testTemperature",
      dlTest.testTemperature,
      dlTest.testTemperature_UOM || ""
    );
    pushField(
      "Saturation Pressure",
      "saturationPressure",
      dlTest.saturationPressure,
      dlTest.saturationPressure_UOM || ""
    );
  }

  const location = sampleData?.location || {};
  pushField("Field", "field", location.field);
  pushField("Well", "well", location.well);
  pushField("Asset", "asset", location.asset);

  return fields;
}

export function formatPvtDlHeaderForParent(sampleData, dlTest) {
  return buildPvtDlHeaderFields(sampleData, dlTest).map((field) => ({
    key: field.originalKey,
    displayName: field.displayName,
    value: field.value,
    digits: field.digits ?? 0,
  }));
}

export async function loadDlTestFromRoute(route, apiGet) {
  const sampleId = route?.params?.sampleId;
  const dlId = route?.params?.id;

  if (isPvtTestRoute(route)) {
    const apiSampleId = getPvtApiSampleId(route);
    if (!apiSampleId) {
      return { ok: false, message: "Sample identifier is missing." };
    }

    const response = await apiGet(getPvtTestListEndpoint(apiSampleId, "dltests"));
    const statusCode = response?.data?.statusCode;
    if (!(statusCode === 200 || statusCode === 201)) {
      return {
        ok: false,
        message: response?.data?.message || "Error in fetching data",
      };
    }

    const payload = response?.data?.data;
    const { sampleMeta, dlTest } = extractDlTestFromResponse(payload, dlId);
    if (!dlTest) {
      return {
        ok: false,
        message: response?.data?.message || "AI Predicted DL test not found.",
      };
    }

    return {
      ok: true,
      isPvt: true,
      dltest: dlTest,
      pageData: payload?.pageData || {},
      sampleMeta: { ...(payload || {}), ...(sampleMeta || {}) },
    };
  }

  if (!sampleId || !dlId) {
    return { ok: false, message: "Sample or test is not specified in the URL." };
  }

  const response = await apiGet(`public/api/samples/${sampleId}/dltests/${dlId}`);
  const statusCode = response?.data?.statusCode;
  if (!(statusCode === 200 || statusCode === 201)) {
    return {
      ok: false,
      message: response?.data?.message || "Error in fetching data",
    };
  }

  return {
    ok: true,
    isPvt: false,
    dltest: response?.data?.data?.dltest,
    pageData: response?.data?.data?.pageData || {},
    sampleMeta: null,
  };
}
